import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, X,
  Sparkles, Layers, PenLine, Loader, Check, Info, Save, Trash2, Printer,
  Mail, Send, Users, AlertTriangle,
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { employesApi, planningApi } from '../../utils/api';

// ─── Constantes ──────────────────────────────────────────────
const CELL_W = 18;
const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
const EMP_COLORS = [
  { bg: 'bg-blue-300', light: 'bg-blue-50', text: 'text-blue-700' },
  { bg: 'bg-purple-300', light: 'bg-purple-50', text: 'text-purple-700' },
  { bg: 'bg-emerald-300', light: 'bg-emerald-50', text: 'text-emerald-700' },
  { bg: 'bg-amber-300', light: 'bg-amber-50', text: 'text-amber-700' },
  { bg: 'bg-rose-300', light: 'bg-rose-50', text: 'text-rose-700' },
  { bg: 'bg-cyan-300', light: 'bg-cyan-50', text: 'text-cyan-700' },
  { bg: 'bg-indigo-300', light: 'bg-indigo-50', text: 'text-indigo-700' },
  { bg: 'bg-orange-300', light: 'bg-orange-50', text: 'text-orange-700' },
];

// Créneaux de 15 min de 7H00 à 18H45 (48 cellules = 12 heures × 4)
const TIME_SLOTS = [];
for (let h = 7; h <= 18; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_SLOTS.push({
      hour: h, minute: m,
      label: `${h}H${m === 0 ? '' : m.toString().padStart(2, '0')}`,
      time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
      minutes: h * 60 + m,
    });
  }
}

function timeToMin(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function isCellFilled(cellMin, slots) {
  return slots.some(s => cellMin >= timeToMin(s.heure_debut) && cellMin < timeToMin(s.heure_fin));
}

function calcDayHours(daySlots) {
  return daySlots.reduce((t, s) => t + (timeToMin(s.heure_fin) - timeToMin(s.heure_debut)) / 60, 0);
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDates(monday) {
  return Array.from({ length: 5 }, (_, i) => addDays(monday, i));
}

// ─── SlotEditor (modal) ──────────────────────────────────────
const ACTIVITES = ['Réunion', 'ADP (analyse de pratiques)', 'Formation', 'Courses', 'Médiathèque', 'Ménage', 'Transmissions', 'Sortie', 'Journée pédagogique'];
function SlotEditor({ employe, dateLabel, slots, onAdd, onDelete, onClose }) {
  const [debut, setDebut] = useState('08:00');
  const [fin, setFin] = useState('17:00');
  const [activite, setActivite] = useState('');
  const [adding, setAdding] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-80 overflow-hidden">
        <div className="bg-slate-800 text-white px-4 py-3 flex items-center justify-between">
          <div>
            <p className="font-semibold text-sm">{employe.prenom} {employe.nom}</p>
            <p className="text-slate-300 text-xs">{dateLabel}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          {slots.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-2">Aucun créneau ce jour</p>
          ) : (
            <div className="space-y-1.5">
              {slots.map(s => (
                <div key={s.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-slate-700 min-w-0">
                    {s.heure_debut} → {s.heure_fin}
                    <span className="text-slate-400 ml-2 text-xs">
                      ({((timeToMin(s.heure_fin) - timeToMin(s.heure_debut)) / 60).toFixed(2)}h)
                    </span>
                    {s.poste && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-navy-100 text-navy-700 align-middle">{s.poste}</span>}
                  </span>
                  <button onClick={() => onDelete(s.id)}
                    className="text-slate-400 hover:text-red-500 p-1 flex-shrink-0"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          )}

          {adding ? (
            <div className="border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Début</label>
                  <input type="time" value={debut} onChange={e => setDebut(e.target.value)}
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Fin</label>
                  <input type="time" value={fin} onChange={e => setFin(e.target.value)}
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Activité (facultatif)</label>
                <input list="activites-liste" value={activite} onChange={e => setActivite(e.target.value)}
                  placeholder="Travail (laisser vide) ou : réunion, ADP…"
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" />
                <datalist id="activites-liste">{ACTIVITES.map(a => <option key={a} value={a} />)}</datalist>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { onAdd(debut, fin, activite.trim() || null); setAdding(false); setActivite(''); }}
                  className="flex-1 bg-primary-500 text-white text-sm rounded py-1.5 flex items-center justify-center gap-1">
                  <Check size={13} /> Ajouter
                </button>
                <button onClick={() => setAdding(false)}
                  className="px-3 py-1.5 text-sm text-slate-500 border border-slate-200 rounded">Annuler</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)}
              className="w-full text-sm text-primary-600 hover:bg-primary-50 border border-dashed border-primary-300 rounded-lg py-2 flex items-center justify-center gap-1">
              <Plus size={14} /> Ajouter un créneau
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TimelineGrid ─────────────────────────────────────────────
function TimelineGrid({ employes, weekDates, slots, onCellClick, readOnly, isTemplate }) {
  const getSlotsFor = (empId, date, dayIdx) => {
    if (isTemplate) {
      const js = dayIdx + 1;
      return slots.filter(s => parseInt(s.employe_id) === parseInt(empId) && s.jour_semaine === js);
    }
    const dateStr = format(date, 'yyyy-MM-dd');
    return slots.filter(s => parseInt(s.employe_id) === parseInt(empId) && s.date === dateStr);
  };

  // Totaux hebdo par employé
  const weeklyTotals = {};
  employes.forEach(emp => {
    weeklyTotals[emp.id] = weekDates.reduce((total, date, dayIdx) => {
      return total + calcDayHours(getSlotsFor(emp.id, date, dayIdx));
    }, 0);
  });

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        {weekDates.map((date, dayIdx) => {
          const dayLabel = isTemplate ? JOURS[dayIdx] : format(date, 'EEEE d MMMM', { locale: fr });
          return (
            <div key={dayIdx} className="print-day">
              {/* En-tête du jour */}
              <div className="bg-slate-700 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide sticky left-0">
                {dayLabel}
              </div>

              {/* En-tête horaire */}
              <div className="flex border-b border-slate-200 bg-slate-50 sticky top-0">
                <div className="flex-shrink-0 w-28 min-w-[112px] px-2 py-1 text-xs text-slate-500 font-medium border-r border-slate-200 sticky left-0 bg-slate-50 z-10">
                  Employé
                </div>
                <div className="flex">
                  {HOURS.map(h => (
                    <div key={h} className="flex">
                      {[0, 15, 30, 45].map(m => (
                        <div key={m}
                          style={{ width: CELL_W, minWidth: CELL_W }}
                          className={`text-center border-r ${m === 0 ? 'border-slate-300' : 'border-slate-100'}`}>
                          {m === 0 && <span className="text-[8px] text-slate-400 leading-none">{h}H</span>}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="flex-shrink-0 w-14 min-w-[56px] px-1 py-1 text-xs text-slate-500 font-medium text-right border-l border-slate-200">
                  Heures
                </div>
              </div>

              {/* Lignes employés */}
              {employes.map((emp, empIdx) => {
                const daySlots = getSlotsFor(emp.id, date, dayIdx);
                const dayTotal = calcDayHours(daySlots);
                const color = EMP_COLORS[empIdx % EMP_COLORS.length];

                return (
                  <div key={emp.id} className="flex border-b border-slate-100 hover:bg-slate-50/50 group">
                    {/* Nom */}
                    <div
                      className={`flex-shrink-0 w-28 min-w-[112px] px-2 py-1 flex items-center gap-1.5 border-r border-slate-200 sticky left-0 bg-white z-10 ${!readOnly ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                      onClick={() => !readOnly && onCellClick && onCellClick(emp, date, dayIdx)}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${color.bg}`} />
                      <span className="text-xs font-medium text-slate-700 truncate">{emp.prenom}</span>
                      {!readOnly && (
                        <Plus size={10} className="text-slate-300 group-hover:text-primary-400 ml-auto flex-shrink-0" />
                      )}
                    </div>

                    {/* Cellules timeline — présence + pause repas + activités */}
                    <div className="flex">
                      {(() => {
                        const iv = daySlots.map(s => [timeToMin(s.heure_debut), timeToMin(s.heure_fin), s.poste]);
                        const spanStart = iv.length ? Math.min(...iv.map(m => m[0])) : null;
                        const spanEnd = iv.length ? Math.max(...iv.map(m => m[1])) : null;
                        return TIME_SLOTS.map((ts, i) => {
                          const cov = iv.find(m => ts.minutes >= m[0] && ts.minutes < m[1]);
                          const filled = !!cov;
                          const activite = cov && cov[2];
                          const isBreak = !filled && spanStart != null && ts.minutes >= spanStart && ts.minutes < spanEnd;
                          const style = { width: CELL_W, minWidth: CELL_W, height: 26 };
                          if (activite) style.backgroundImage = 'repeating-linear-gradient(45deg, rgba(255,255,255,.6) 0 3px, transparent 3px 6px)';
                          else if (isBreak) style.backgroundImage = 'repeating-linear-gradient(45deg, #f59e0b40 0 3px, transparent 3px 6px)';
                          return (
                            <div
                              key={i}
                              style={style}
                              className={`border-r ${ts.minute === 0 ? 'border-slate-200' : 'border-slate-50'} ${
                                filled ? color.bg : ''
                              } ${!readOnly ? 'cursor-pointer' : ''}`}
                              onClick={() => !readOnly && onCellClick && onCellClick(emp, date, dayIdx)}
                              title={activite ? `${ts.label} · ${activite}` : (isBreak ? 'Pause repas' : (filled ? ts.label : ''))}
                            />
                          );
                        });
                      })()}
                    </div>

                    {/* Total jour */}
                    <div className="flex-shrink-0 w-14 min-w-[56px] px-1 flex items-center justify-end border-l border-slate-200">
                      <span className={`text-xs font-semibold ${dayTotal > 0 ? 'text-slate-700' : 'text-slate-300'}`}>
                        {dayTotal > 0 ? dayTotal.toFixed(dayTotal % 1 === 0 ? 0 : 2) + 'h' : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Résumé hebdomadaire */}
      <div className="bg-slate-50 border-t border-slate-200 px-3 py-3">
        <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Totaux semaine</p>
        <div className="flex flex-wrap gap-3">
          {employes.map((emp, empIdx) => {
            const color = EMP_COLORS[empIdx % EMP_COLORS.length];
            const total = weeklyTotals[emp.id] || 0;
            return (
              <div key={emp.id} className={`${color.light} border rounded-lg px-3 py-1.5 flex items-center gap-2`}>
                <div className={`w-2 h-2 rounded-full ${color.bg}`} />
                <span className={`text-xs font-medium ${color.text}`}>{emp.prenom}</span>
                <span className={`text-sm font-bold ${color.text}`}>
                  {total.toFixed(total % 1 === 0 ? 0 : 2)}h
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── NotifyModal : validation de l'envoi du planning aux équipes ──
function NotifyModal({ membres, semaineLabel, sending, result, onSend, onClose }) {
  const avecEmail = membres.filter(m => m.email);
  const sansEmail = membres.filter(m => !m.email);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={e => { if (e.target === e.currentTarget && !sending) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden">
        <div className="bg-navy-700 text-white px-5 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0"><Mail size={18} /></div>
          <div>
            <p className="font-semibold text-[15px]">Prévenir les équipes du changement</p>
            <p className="text-white/70 text-xs">Semaine {semaineLabel}</p>
          </div>
        </div>

        {result ? (
          <div className="p-5 space-y-3">
            {result.envoyes?.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2.5 text-sm text-green-800">
                <div className="flex items-center gap-1.5 font-semibold mb-1"><Check size={15} /> Planning envoyé à {result.envoyes.length} membre{result.envoyes.length > 1 ? 's' : ''}</div>
                <div className="text-green-700 text-[13px]">{result.envoyes.join(', ')}</div>
              </div>
            )}
            {result.sans_email?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-[13px] text-amber-800">
                <span className="font-semibold">Sans adresse email (non envoyé) :</span> {result.sans_email.join(', ')}. Ajoutez leur email dans leur fiche RH.
              </div>
            )}
            {result.erreurs?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-[13px] text-red-800">
                <span className="font-semibold">Échecs :</span> {result.erreurs.join(' · ')}
              </div>
            )}
            <button onClick={onClose} className="w-full bg-navy-600 hover:bg-navy-700 text-white py-2.5 rounded-lg text-sm font-semibold">Fermer</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-sm text-slate-600">
              Chaque membre concerné par vos modifications recevra un email avec <strong>son planning à jour</strong> pour cette semaine.
            </p>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
              {membres.map(m => (
                <div key={m.id} className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm font-medium text-slate-700">{m.prenom} {m.nom}</span>
                  {m.email
                    ? <span className="text-xs text-slate-500 truncate max-w-[190px]">{m.email}</span>
                    : <span className="text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">sans email</span>}
                </div>
              ))}
            </div>
            {sansEmail.length > 0 && (
              <div className="flex items-start gap-2 text-[12.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{sansEmail.length} membre{sansEmail.length > 1 ? 's' : ''} sans adresse email ne recevr{sansEmail.length > 1 ? 'ont' : 'a'} pas le planning.</span>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={onSend} disabled={sending || avecEmail.length === 0}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {sending ? <Loader size={15} className="animate-spin" /> : <Send size={15} />}
                {sending ? 'Envoi…' : `Envoyer à ${avecEmail.length} membre${avecEmail.length > 1 ? 's' : ''}`}
              </button>
              <button onClick={onClose} disabled={sending}
                className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm disabled:opacity-50">Plus tard</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────
export default function PlanningPage() {
  const { structures } = useApp();
  const { user } = useAuth();
  // Modification/création réservée à certains comptes (Chloé + Marion) ; les autres sont en consultation.
  const canEdit = user?.role === 'admin' || !!user?.peut_modifier_planning;
  const [mode, setMode] = useState('manuel');
  const [semaine, setSemaine] = useState(() => getMonday(new Date()));
  const [selectedStructure, setSelectedStructure] = useState('');
  const [employes, setEmployes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [templateSlots, setTemplateSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaSlots, setIaSlots] = useState(null);
  const [iaResume, setIaResume] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // Membres concernés par des modifications non encore notifiées (ids)
  const [pendingNotify, setPendingNotify] = useState([]);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const markAffected = (empId) => {
    const id = parseInt(empId);
    setPendingNotify(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  // Slot editor state
  const [editorTarget, setEditorTarget] = useState(null); // { employe, date, dayIdx }

  const weekDates = getWeekDates(semaine);
  const structureNom = structures.find(s => String(s.id) === String(selectedStructure))?.nom || '';

  // Le planning d'une crèche peut inclure des salariées d'une autre structure
  // (renfort inter-crèches). On affiche donc les employées de la structure PLUS
  // toute personne ayant un créneau cette semaine dans cette structure.
  const gridEmployes = useMemo(() => {
    const byId = new Map((employes || []).map(e => [parseInt(e.id), e]));
    (slots || []).forEach(s => {
      const id = parseInt(s.employe_id);
      if (!byId.has(id)) byId.set(id, { id, prenom: s.employe_prenom, nom: s.employe_nom, poste: s.employe_poste });
    });
    return Array.from(byId.values());
  }, [employes, slots]);

  // Charger les employés
  useEffect(() => {
    if (!selectedStructure) { setEmployes([]); return; }
    employesApi.getAll({ structure_id: selectedStructure })
      .then(res => setEmployes(res.data || []))
      .catch(() => setEmployes([]));
  }, [selectedStructure]);

  // Charger les slots
  const loadSlots = useCallback(async () => {
    if (!selectedStructure) return;
    setLoading(true);
    try {
      const res = await planningApi.getSlots({
        structure_id: selectedStructure,
        date_debut: format(weekDates[0], 'yyyy-MM-dd'),
        date_fin: format(weekDates[4], 'yyyy-MM-dd'),
      });
      setSlots(res.data || []);
    } catch { setSlots([]); }
    finally { setLoading(false); }
  }, [selectedStructure, semaine]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplate = useCallback(async () => {
    if (!selectedStructure) return;
    try {
      const res = await planningApi.getTemplate(selectedStructure);
      setTemplateSlots(res.data || []);
    } catch { setTemplateSlots([]); }
  }, [selectedStructure]);

  useEffect(() => { loadSlots(); }, [loadSlots]);
  useEffect(() => { if (mode === 'mixte') loadTemplate(); }, [loadTemplate, mode]);
  // Les changements en attente de notification sont propres à une structure/semaine.
  useEffect(() => { setPendingNotify([]); setNotifyOpen(false); setNotifyResult(null); }, [selectedStructure, semaine]);

  // ─── Actions ──────────────────────────────────────────────
  const handleAddSlot = async (empId, dateStr, debut, fin, activite = null) => {
    try {
      await planningApi.createSlot({
        structure_id: selectedStructure,
        employe_id: empId,
        date: dateStr,
        heure_debut: debut,
        heure_fin: fin,
        poste: activite || null,
      });
      loadSlots();
    } catch { alert('Erreur lors de l\'ajout'); }
  };

  const handleDeleteSlot = async (id) => {
    try { await planningApi.deleteSlot(id); loadSlots(); }
    catch { alert('Erreur lors de la suppression'); }
  };

  const handleAddTemplateSlot = async (empId, dateStr, debut, fin, jourSemaine, activite = null) => {
    try {
      await planningApi.createTemplateSlot({
        structure_id: selectedStructure,
        employe_id: empId,
        jour_semaine: jourSemaine,
        heure_debut: debut,
        heure_fin: fin,
        poste: activite || null,
      });
      loadTemplate();
    } catch { alert('Erreur'); }
  };

  const handleDeleteTemplateSlot = async (id) => {
    try { await planningApi.deleteTemplateSlot(id); loadTemplate(); }
    catch { alert('Erreur'); }
  };

  const handleGenerateIA = async () => {
    if (!selectedStructure) return alert('Sélectionnez une structure');
    setIaLoading(true); setIaSlots(null); setIaResume('');
    try {
      const res = await planningApi.genererIA({
        structure_id: selectedStructure,
        date_debut: format(weekDates[0], 'yyyy-MM-dd'),
        date_fin: format(weekDates[4], 'yyyy-MM-dd'),
      });
      setIaSlots(res.data.slots || []);
      setIaResume(res.data.resume || '');
    } catch (err) {
      alert('Erreur IA : ' + (err.response?.data?.error || err.message));
    }
    setIaLoading(false);
  };

  const handleValidateIA = async () => {
    if (!iaSlots || iaSlots.length === 0) return;
    setSaving(true);
    try {
      for (const slot of iaSlots) {
        await planningApi.createSlot({
          structure_id: selectedStructure,
          employe_id: slot.employe_id,
          date: slot.date,
          heure_debut: slot.heure_debut,
          heure_fin: slot.heure_fin,
          poste: slot.poste,
        });
      }
      // Tous les membres présents dans la proposition sont concernés → à prévenir
      const ids = [...new Set(iaSlots.map(s => parseInt(s.employe_id)))];
      setPendingNotify(prev => [...new Set([...prev, ...ids])]);
      setIaSlots(null); setIaResume(''); setMode('manuel');
      loadSlots();
      setSavedMsg('Planning enregistré !');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch { alert('Erreur'); }
    setSaving(false);
  };

  const handleApplyTemplate = async () => {
    if (!selectedStructure) return;
    setIaLoading(true); setIaSlots(null);
    try {
      const res = await planningApi.appliquerTemplate({
        structure_id: selectedStructure,
        date_debut: format(weekDates[0], 'yyyy-MM-dd'),
      });
      setIaSlots(res.data.slots || []);
      setIaResume(res.data.resume || '');
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
    setIaLoading(false);
  };

  // Slot editor handlers
  const openEditor = (employe, date, dayIdx) => {
    if (!canEdit) return;
    setEditorTarget({ employe, date, dayIdx });
  };

  const getEditorSlots = () => {
    if (!editorTarget) return [];
    const { employe, date, dayIdx } = editorTarget;

    if (mode === 'mixte' && !iaSlots) {
      // Editing template
      const js = dayIdx + 1;
      return templateSlots.filter(s => parseInt(s.employe_id) === parseInt(employe.id) && s.jour_semaine === js);
    }

    if (iaSlots) {
      const dateStr = format(date, 'yyyy-MM-dd');
      return iaSlots.filter(s => parseInt(s.employe_id) === parseInt(employe.id) && s.date === dateStr);
    }

    const dateStr = format(date, 'yyyy-MM-dd');
    return slots.filter(s => parseInt(s.employe_id) === parseInt(employe.id) && s.date === dateStr);
  };

  const handleEditorAdd = (debut, fin, activite = null) => {
    if (!editorTarget) return;
    const { employe, date, dayIdx } = editorTarget;

    if (mode === 'mixte' && !iaSlots) {
      handleAddTemplateSlot(employe.id, null, debut, fin, dayIdx + 1, activite);
    } else if (iaSlots) {
      setIaSlots(prev => [...prev, {
        id: 'ia_' + Date.now(),
        employe_id: parseInt(employe.id),
        date: format(date, 'yyyy-MM-dd'),
        heure_debut: debut,
        heure_fin: fin,
        poste: activite || null,
      }]);
    } else {
      handleAddSlot(employe.id, format(date, 'yyyy-MM-dd'), debut, fin, activite);
      markAffected(employe.id); // planning réel modifié → membre à prévenir
    }
  };

  const handleEditorDelete = (id) => {
    if (mode === 'mixte' && !iaSlots) {
      handleDeleteTemplateSlot(id);
    } else if (iaSlots) {
      setIaSlots(prev => prev.filter(s => s.id !== id));
    } else {
      handleDeleteSlot(id);
      if (editorTarget) markAffected(editorTarget.employe.id); // membre à prévenir
    }
  };

  // ─── Notification des équipes ─────────────────────────────
  const membresConcernes = pendingNotify
    .map(id => gridEmployes.find(e => parseInt(e.id) === parseInt(id)))
    .filter(Boolean);
  const semaineLabel = `du ${format(weekDates[0], 'd MMM', { locale: fr })} au ${format(weekDates[4], 'd MMM yyyy', { locale: fr })}`;

  const handleSendNotify = async () => {
    setNotifySending(true);
    try {
      const res = await planningApi.notifier({
        structure_id: selectedStructure,
        date_debut: format(weekDates[0], 'yyyy-MM-dd'),
        date_fin: format(weekDates[4], 'yyyy-MM-dd'),
        employe_ids: pendingNotify,
      });
      setNotifyResult(res.data);
      // Les membres réellement notifiés ne sont plus « en attente »
      setPendingNotify([]);
    } catch (err) {
      setNotifyResult({ envoyes: [], sans_email: [], erreurs: [err.response?.data?.error || err.message] });
    }
    setNotifySending(false);
  };

  const MODES = [
    { key: 'manuel', icon: PenLine, label: 'Manuel', desc: 'Saisie libre chaque semaine', color: 'border-navy-300 bg-navy-50 text-navy-700' },
    { key: 'ia', icon: Sparkles, label: 'IA', desc: 'Optimisation automatique', color: 'border-gold-300 bg-gold-50 text-gold-700' },
    { key: 'mixte', icon: Layers, label: 'Mixte', desc: 'Trame + adaptation IA', color: 'border-gold-200 bg-cream-100 text-gold-700' },
  ];

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gold-100 flex items-center justify-center">
            <CalendarDays size={18} className="text-gold-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Planning</h1>
            <p className="text-slate-500 text-sm">Grille horaire par quart d'heure</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!canEdit && <span className="text-[12px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">👁️ Consultation</span>}
          {savedMsg && <span className="text-sm text-green-600 font-medium flex items-center gap-1"><Check size={14} /> {savedMsg}</span>}
          <select value={selectedStructure}
            onChange={e => { setSelectedStructure(e.target.value); setIaSlots(null); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
            <option value="">— Structure —</option>
            {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
      </div>

      {/* Bandeau : des changements sont en attente d'envoi aux équipes */}
      {canEdit && membresConcernes.length > 0 && (
        <div className="no-print sticky top-2 z-20 flex items-center justify-between gap-3 flex-wrap bg-gold-50 border border-gold-300 rounded-xl px-4 py-3 shadow-sm">
          <div className="flex items-center gap-2.5 text-[13.5px] text-gold-800">
            <Users size={18} className="flex-shrink-0 text-gold-600" />
            <span>
              <strong>{membresConcernes.length} membre{membresConcernes.length > 1 ? 's' : ''}</strong> concerné{membresConcernes.length > 1 ? 's' : ''} par vos modifications&nbsp;:
              {' '}{membresConcernes.map(m => m.prenom).join(', ')}.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setNotifyResult(null); setNotifyOpen(true); }}
              className="inline-flex items-center gap-1.5 bg-navy-600 hover:bg-navy-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg">
              <Mail size={15} /> Valider l'envoi du planning
            </button>
            <button onClick={() => setPendingNotify([])} title="Ignorer sans envoyer"
              className="text-gold-700/70 hover:text-gold-900 p-1.5"><X size={16} /></button>
          </div>
        </div>
      )}

      {/* Mode selector — uniquement pour les comptes autorisés à modifier */}
      {canEdit && (
      <div className="grid grid-cols-3 gap-2">
        {MODES.map(m => (
          <button key={m.key}
            onClick={() => { setMode(m.key); setIaSlots(null); setIaResume(''); }}
            className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${
              mode === m.key ? m.color : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
            }`}>
            <m.icon size={18} className="flex-shrink-0" />
            <div>
              <div className="font-semibold text-xs">{m.label}</div>
              <div className="text-[10px] opacity-70">{m.desc}</div>
            </div>
          </button>
        ))}
      </div>
      )}

      {/* Week navigation */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-2.5">
        <button onClick={() => setSemaine(d => addDays(d, -7))}
          className="p-1 rounded-lg hover:bg-slate-100 text-slate-500"><ChevronLeft size={18} /></button>
        <div className="text-center">
          <span className="text-sm font-semibold text-slate-700">
            {format(weekDates[0], 'd MMMM', { locale: fr })} — {format(weekDates[4], 'd MMMM yyyy', { locale: fr })}
          </span>
        </div>
        <button onClick={() => setSemaine(d => addDays(d, 7))}
          className="p-1 rounded-lg hover:bg-slate-100 text-slate-500"><ChevronRight size={18} /></button>
      </div>

      {/* Contenu */}
      {!selectedStructure ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-200 py-16 text-center">
          <CalendarDays size={36} className="mx-auto mb-3 text-slate-200" />
          <p className="text-slate-400 text-sm">Sélectionnez une structure</p>
        </div>
      ) : employes.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-200 py-16 text-center">
          <p className="text-slate-400 text-sm">Aucun employé dans cette structure</p>
          <a href="/rh" className="text-navy-600 text-sm hover:underline">Ajouter →</a>
        </div>
      ) : (
        <>
          {/* ─── MODE MANUEL ─── */}
          {mode === 'manuel' && (
            loading ? (
              <div className="bg-white rounded-xl border border-slate-200 py-12 text-center text-slate-400">
                <Loader size={20} className="animate-spin mx-auto mb-2" /> Chargement...
              </div>
            ) : (
              <div>
                <div className="no-print flex justify-end mb-2">
                  <button onClick={() => window.print()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm hover:border-navy-300 hover:text-navy-800">
                    <Printer size={15} /> Imprimer / Export A4 (PDF)
                  </button>
                </div>
                <div className="print-area">
                  {/* En-tête visible uniquement à l'impression */}
                  <div className="print-only mb-2">
                    <div className="text-[15px] font-bold text-black">{structureNom} — Planning équipe</div>
                    <div className="text-[12px] text-black">
                      Semaine du {format(weekDates[0], 'd MMMM', { locale: fr })} au {format(weekDates[4], 'd MMMM yyyy', { locale: fr })}
                    </div>
                  </div>
                  <TimelineGrid
                    employes={gridEmployes}
                    weekDates={weekDates}
                    slots={slots}
                    onCellClick={openEditor}
                    readOnly={!canEdit}
                  />
                  {/* Légende */}
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-300" /> Présence</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #f59e0b40 0 3px, transparent 3px 6px)', backgroundColor: '#fff', border: '1px solid #e2e8f0' }} /> Pause repas</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-300" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,.6) 0 3px, transparent 3px 6px)' }} /> Activité (réunion, ADP…)</span>
                    {canEdit && <span className="text-slate-400">· Cliquez une ligne pour ajouter un créneau ou une activité.</span>}
                  </div>
                </div>
              </div>
            )
          )}

          {/* ─── MODE IA ─── */}
          {mode === 'ia' && (
            <div className="space-y-4">
              <div className="bg-gold-50 border border-gold-200 rounded-xl px-4 py-3 text-sm text-gold-700 flex gap-2">
                <Info size={16} className="flex-shrink-0 mt-0.5" />
                <p>Georges analyse les contrats enfants, les absences et l'agrément PMI pour proposer un planning optimal.</p>
              </div>

              {!iaSlots ? (
                <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
                  <Sparkles size={32} className="mx-auto mb-4 text-gold-300" />
                  <p className="font-semibold text-slate-700 mb-1">Générer le planning IA</p>
                  <p className="text-slate-400 text-sm mb-6">Semaine du {format(weekDates[0], 'd MMM', { locale: fr })}</p>
                  <button onClick={handleGenerateIA} disabled={iaLoading}
                    className="bg-gold-500 hover:bg-gold-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 mx-auto disabled:opacity-50">
                    {iaLoading ? <Loader size={15} className="animate-spin" /> : <Sparkles size={15} />}
                    {iaLoading ? 'Génération...' : 'Générer'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {iaResume && (
                    <div className="bg-gold-50 border border-gold-200 rounded-xl px-4 py-3 text-sm text-gold-700">
                      <strong>Résumé IA :</strong> {iaResume}
                    </div>
                  )}
                  <TimelineGrid employes={employes} weekDates={weekDates} slots={iaSlots} onCellClick={openEditor} />
                  <div className="flex gap-3">
                    <button onClick={handleValidateIA} disabled={saving}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                      {saving ? <Loader size={15} className="animate-spin" /> : <Save size={15} />}
                      Valider et enregistrer
                    </button>
                    <button onClick={() => { setIaSlots(null); setIaResume(''); }}
                      className="px-5 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm">Recommencer</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── MODE MIXTE ─── */}
          {mode === 'mixte' && (
            <div className="space-y-5">
              {/* Trame */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Layers size={16} className="text-gold-500" />
                  <h3 className="font-semibold text-slate-700 text-sm">Semaine type (trame)</h3>
                </div>
                <TimelineGrid
                  employes={employes}
                  weekDates={weekDates}
                  slots={templateSlots}
                  onCellClick={!iaSlots ? openEditor : undefined}
                  readOnly={!!iaSlots}
                  isTemplate={true}
                />
              </div>

              {/* Application */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                {templateSlots.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 mb-3">
                    Ajoutez d'abord des créneaux dans la trame ci-dessus.
                  </div>
                )}
                {!iaSlots ? (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-600">
                      Appliquer à la semaine du <strong>{format(weekDates[0], 'd MMM', { locale: fr })}</strong>
                    </p>
                    <button onClick={handleApplyTemplate} disabled={iaLoading || templateSlots.length === 0}
                      className="bg-gold-500 hover:bg-gold-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                      {iaLoading ? <Loader size={15} className="animate-spin" /> : <Layers size={15} />}
                      {iaLoading ? 'Application...' : 'Appliquer'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {iaResume && (
                      <div className="bg-gold-50 border border-gold-200 rounded-lg px-3 py-2 text-sm text-gold-700">
                        <strong>Résultat :</strong> {iaResume}
                      </div>
                    )}
                    <TimelineGrid employes={employes} weekDates={weekDates} slots={iaSlots} onCellClick={openEditor} />
                    <div className="flex gap-3">
                      <button onClick={handleValidateIA} disabled={saving}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                        {saving ? <Loader size={15} className="animate-spin" /> : <Save size={15} />}
                        Valider
                      </button>
                      <button onClick={() => { setIaSlots(null); setIaResume(''); }}
                        className="px-5 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm">Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Slot Editor Modal */}
      {editorTarget && (
        <SlotEditor
          employe={editorTarget.employe}
          dateLabel={
            mode === 'mixte' && !iaSlots
              ? JOURS[editorTarget.dayIdx]
              : format(editorTarget.date, 'EEEE d MMMM yyyy', { locale: fr })
          }
          slots={getEditorSlots()}
          onAdd={handleEditorAdd}
          onDelete={handleEditorDelete}
          onClose={() => setEditorTarget(null)}
        />
      )}

      {/* Modal : validation de l'envoi du planning aux équipes */}
      {notifyOpen && (
        <NotifyModal
          membres={membresConcernes}
          semaineLabel={semaineLabel}
          sending={notifySending}
          result={notifyResult}
          onSend={handleSendNotify}
          onClose={() => { setNotifyOpen(false); setNotifyResult(null); }}
        />
      )}
    </div>
  );
}
